import type { IntlShape } from "@formatjs/intl";
import { Empty, Table, Typography } from "antd";
import { isMaterialUsageRecorded } from "../../../../common/utils/serviceMaterials";
import {
  groupSuppliesByCategory,
  supplyCategoryMessageId,
} from "../../../../common/utils/supplyCategories";
import type { TicketMaterialsUsage } from "../../../generated-axios-client";
import { measurementUnitLabelMap } from "../../ticketConstants";

type SupplyRow = { key: string; category: string | null; usage: TicketMaterialsUsage };

type ReportSuppliesPreviewProps = {
  materials: TicketMaterialsUsage[];
  materialCatalog: Record<string, string>;
  categoryCatalog: Record<string, string | null>;
  intl: IntlShape;
};

const emptyValue = <Typography.Text type="secondary">—</Typography.Text>;

/** The Used Supplies table as the PDF prints it: only supplies with a recorded use. */
export function ReportSuppliesPreview({
  materials,
  materialCatalog,
  categoryCatalog,
  intl,
}: ReportSuppliesPreviewProps) {
  const materialName = (usage: TicketMaterialsUsage) =>
    usage.material ? (materialCatalog[usage.material] ?? "") : "";

  const rows: SupplyRow[] = groupSuppliesByCategory(
    materials.filter(isMaterialUsageRecorded),
    categoryCatalog
  ).flatMap(({ category, supplies }) =>
    [...supplies]
      .sort((first, second) => materialName(first).localeCompare(materialName(second)))
      .map((usage) => ({ key: usage.id ?? materialName(usage), category, usage }))
  );

  if (rows.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={intl.formatMessage({ id: "wo_report_no_supplies" })}
      />
    );
  }

  return (
    <Table<SupplyRow>
      size="small"
      pagination={false}
      dataSource={rows}
      columns={[
        {
          key: "category",
          title: intl.formatMessage({ id: "wo_report_supplies_category" }),
          render: (_, { category }) =>
            intl.formatMessage({ id: supplyCategoryMessageId(category) }),
        },
        {
          key: "material",
          title: intl.formatMessage({ id: "ticket_supplies_column_material" }),
          render: (_, { usage }) => materialName(usage) || emptyValue,
        },
        {
          key: "amount",
          title: intl.formatMessage({ id: "ticket_supplies_column_amount" }),
          render: (_, { usage }) => {
            if (usage.amount == null) return emptyValue;
            const unit = usage.measurement_unit
              ? measurementUnitLabelMap[usage.measurement_unit]
              : undefined;
            return unit ? `${usage.amount} ${unit}` : usage.amount;
          },
        },
        {
          key: "batch",
          title: intl.formatMessage({ id: "ticket_supplies_column_batch" }),
          render: (_, { usage }) => usage.batch ?? emptyValue,
        },
        {
          key: "expiration_date",
          title: intl.formatMessage({ id: "ticket_supply_expiration_date" }),
          render: (_, { usage }) =>
            usage.expiration_date
              ? intl.formatMessage(
                  { id: "short_date" },
                  { value: new Date(usage.expiration_date) }
                )
              : emptyValue,
        },
      ]}
    />
  );
}
